import { useEffect } from 'react';

import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';

import { EXITED_CODE_REVIEW } from 'src/redux/pull-request/actions';
import { UNLOAD_BRANCH_COMMITS } from 'src/redux/repo-commits/actions';
import {
  FETCH_DEST_BRANCH_DATA,
  UNLOAD_BRANCH_STATE,
  getBranchViewDestinationRepoName,
  getBranchViewDestinationBranchName,
} from 'src/redux/repository/branch/branch-redux';
import urls from 'src/sections/repository/urls';
import authRequest from 'src/utils/fetch';

const generateDetailedUrls = (destRepoName: string, destBranchName: string) => {
  const fields =
    'values.permissions,values.ismainbranch,values.merge_strategies,values.default_merge_strategy';
  const [destOwner, destSlug] = destRepoName.split('/');
  const destUrl = urls.api.internal.branchList(destOwner, destSlug);
  const destQuery = qs.stringify(
    {
      branch: destBranchName,
      fields,
    },
    {
      addQueryPrefix: true,
    }
  );
  const destCompleteUrl = `${destUrl}${destQuery}`;
  return { destCompleteUrl };
};

const fetchDestBranchDetails = async (destCompleteUrl: string) => {
  const destResponse = await fetch(authRequest(destCompleteUrl));
  if (!destResponse.ok) {
    throw Error('Error fetching details about destination branch');
  }
  const destResult = await destResponse.json();
  if (destResult.values.length === 0) {
    throw Error('Error fetching details about destination branch');
  }
  const destBranch = destResult.values[0];
  return { destBranch };
};

export const useFetchDetailedBranchInfo = () => {
  const dispatch = useDispatch();
  const destRepoName = useSelector(getBranchViewDestinationRepoName);
  const destBranchName = useSelector(getBranchViewDestinationBranchName);

  useEffect(
    () => {
      if (!destBranchName || !destRepoName) {
        return;
      }
      const { destCompleteUrl } = generateDetailedUrls(
        destRepoName,
        destBranchName
      );
      try {
        fetchDestBranchDetails(destCompleteUrl).then(res => {
          dispatch({
            type: FETCH_DEST_BRANCH_DATA.SUCCESS,
            payload: {
              destination: {
                permissions: res.destBranch.permissions,
                isMainBranch: res.destBranch.ismainbranch,
                default_merge_strategy: res.destBranch.default_merge_strategy,
                merge_strategies: res.destBranch.merge_strategies,
              },
            },
          });
        });
      } catch {
        // TODO: Figure out what to do in case of an error, currently fail silenty. Put into Sentry?
      }
    },
    // Only run when destination changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, destBranchName, destRepoName]
  );
};

export const useUnloadBranch = (): void => {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch({ type: UNLOAD_BRANCH_COMMITS });
      dispatch({ type: UNLOAD_BRANCH_STATE });
      dispatch({ type: EXITED_CODE_REVIEW });
    };
  }, [dispatch]);
};
